import React from "react";
import { motion } from "framer-motion";
import { StaticImage } from "gatsby-plugin-image";
import {
  FiAward,
  FiDownload,
  FiEdit,
  FiCheckCircle,
  FiLayout,
  FiSearch,
  FiUser,
  FiTarget,
  FiTrendingUp,
  FiShield,
} from "react-icons/fi";

const ResumeTemplates = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
  };

  const staggerChildren = {
    animate: {
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const benefitsList = [
    {
      icon: <FiUser className="w-5 h-5" />,
      title: "Professional Branding",
      text: "Stand out with modern designs",
    },
    {
      icon: <FiTarget className="w-5 h-5" />,
      title: "Industry-Specific",
      text: "Tailored for your field",
    },
    {
      icon: <FiTrendingUp className="w-5 h-5" />,
      title: "Career Growth",
      text: "Highlight achievements",
    },
    {
      icon: <FiShield className="w-5 h-5" />,
      title: "ATS-Proof",
      text: "Optimized for job systems",
    },
    {
      icon: <FiEdit className="w-6 h-6" />,
      title: "Smart Editor",
      text: "AI-powered content suggestions",
    },
    {
      icon: <FiDownload className="w-6 h-6" />,
      title: "Multiple Formats",
      text: "Export to PDF, Word, or TXT",
    },
  ];

  return (
    <div className="relative py-24 bg-white overflow-hidden">
      <div className="absolute inset-0 z-0">
        <div className="absolute top-0 left-1/4 w-72 h-72 bg-indigo-50 rounded-full mix-blend-multiply filter blur-xl opacity-70" />
        <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-purple-50 rounded-full mix-blend-multiply filter blur-xl opacity-70" />
      </div>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial="initial"
          animate="animate"
          variants={staggerChildren}
          className="space-y-12"
        >
          {/* SEO-optimized Header Section */}
          <motion.div variants={fadeInUp} className="text-center">
            <span className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
              <FiLayout className="mr-2" /> #1 Professional Resume Builder
            </span>
            <h1 className="mt-4 text-4xl md:text-5xl font-bold text-neutral-900 leading-tight">
              Create Your Perfect{" "}
              <span className="text-indigo-600">Professional Resume</span>
            </h1>
            <p className="mt-6 text-sm md:text-md text-neutral-700 max-w-2xl mx-auto">
              Land your dream job with our professionally designed,
              ATS-optimized resume templates. Trusted by over 100,000+
              professionals across all career levels and industries.
            </p>
          </motion.div>

          {/* Key Benefits Section */}
          <motion.div
            variants={fadeInUp}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12"
          >
            <div className="col-span-full flex flex-wrap justify-center gap-6">
              {benefitsList.map((benefit, index) => (
                <div
                  key={index}
                  className="text-center p-4 w-[calc(50%-12px)] md:w-[calc(25%-18px)]"
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-indigo-100 text-indigo-600 mb-4">
                    {benefit.icon}
                  </div>
                  <h3 className="text-sm font-semibold text-neutral-900">
                    {benefit.title}
                  </h3>
                  <p className="text-xs text-neutral-600 mt-1">
                    {benefit.text}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Templates Showcase */}
          <motion.div variants={fadeInUp}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Template cards remain the same */}
              {/* Template 1 - Professional */}
              <div className="bg-white rounded-lg shadow-lg p-6 flex flex-col items-center text-center hover:shadow-xl transition-shadow">
                <div className="relative w-full mb-4">
                  <StaticImage
                    src="../images/templates/resume/classic.png"
                    alt="Professional Resume Template"
                    placeholder="blurred"
                    layout="constrained"
                    className="rounded-lg"
                  />
                </div>
                <h3 className="text-md font-semibold text-neutral-900">
                  Professional Classic
                </h3>
                <p className="text-sm text-neutral-700 mt-2">
                  Clean layout with clear sections for summary, skills, and
                  experience. Perfect for marketing and business professionals.
                </p>
              </div>

              {/* Template 2 - Modern */}
              <div className="bg-white rounded-lg shadow-lg p-6 flex flex-col items-center text-center hover:shadow-xl transition-shadow">
                <div className="relative w-full mb-4">
                  <StaticImage
                    src="../images/templates/resume/default.png"
                    alt="Modern Resume Template"
                    placeholder="blurred"
                    layout="constrained"
                    className="rounded-lg"
                  />
                </div>
                <h3 className="text-md font-semibold text-neutral-900">
                  Modern Executive
                </h3>
                <p className="text-sm text-neutral-700 mt-2">
                  Structured design with emphasis on professional achievements
                  and certifications. Ideal for experienced professionals.
                </p>
              </div>

              {/* Template 3 - Minimalist */}
              <div className="bg-white rounded-lg shadow-lg p-6 flex flex-col items-center text-center hover:shadow-xl transition-shadow">
                <div className="relative w-full mb-4">
                  <StaticImage
                    src="../images/templates/resume/minimalist.png"
                    alt="Minimalist Resume Template"
                    placeholder="blurred"
                    layout="constrained"
                    className="rounded-lg"
                  />
                </div>
                <h3 className="text-md font-semibold text-neutral-900">
                  Skills Focused
                </h3>
                <p className="text-sm text-neutral-700 mt-2">
                  Highlights technical skills and project experience. Perfect
                  for IT and digital marketing professionals.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Enhanced Call to Action Section */}
          <motion.div
            variants={fadeInUp}
            className="text-center mt-12 space-y-6 bg-gradient-to-b from-white to-indigo-50 rounded-2xl p-8"
          >
            <h3 className="text-2xl font-bold text-neutral-900">
              Start Building Your Professional Resume Today
            </h3>
            <p className="text-neutral-700 max-w-xl mx-auto">
              Join thousands of successful professionals who have landed their
              dream jobs using our templates
            </p>
            <div className="flex items-center justify-center space-x-4 text-sm text-neutral-600">
              <span className="flex items-center">
                <FiCheckCircle className="mr-2 text-indigo-600" />
                ATS-Friendly
              </span>
              <span className="flex items-center">
                <FiCheckCircle className="mr-2 text-indigo-600" />
                Easy to Use
              </span>
              <span className="flex items-center">
                <FiCheckCircle className="mr-2 text-indigo-600" />
                Instant Download
              </span>
            </div>
            <button
              className="bg-indigo-600 text-white px-8 py-4 rounded-lg text-sm font-medium hover:bg-indigo-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
              onClick={() =>
                window.open("https://app.resumeflex.com/auth", "_blank")
              }
            >
              Create Your Professional Resume Now
            </button>
            <p className="text-xs text-neutral-500">
              Get Your Pass Now, No Subscription Needed!
            </p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default ResumeTemplates;
